import { useQuery, useMutation } from "@tanstack/react-query";
import {
    fetchBillingPlan,
    fetchBillingInvoices,
    createPortalSession,
    createBillingCheckoutSession,
} from "@common/lib/billingApi";
import { toast } from "react-toastify";
import type { BillingPlan, BillingInvoice } from "@common/utils/types";

export const billingPlanQueryKey = ["billing-plan"] as const;
export const billingInvoicesQueryKey = ["billing-invoices"] as const;

export function useBillingPlan(options?: { enabled?: boolean }) {
    return useQuery<BillingPlan>({
        queryKey: billingPlanQueryKey,
        queryFn: fetchBillingPlan,
        staleTime: 2 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        enabled: options?.enabled ?? true,
    });
}

export function useBillingInvoices() {
    return useQuery<BillingInvoice[]>({
        queryKey: billingInvoicesQueryKey,
        queryFn: fetchBillingInvoices,
        staleTime: 2 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
}

function extractErrorMessage(error: unknown, fallback: string): string {
    if (error && typeof error === "object" && "response" in error) {
        const resp = (error as any).response;
        const body = resp?.data ?? resp?.json;
        if (body?.error) return body.error;
        if (body?.message) return body.message;
    }
    return fallback;
}

export function useCreatePortalSession() {
    return useMutation({
        mutationFn: createPortalSession,
        onSuccess: (data) => {
            if (!data.url) {
                toast.error("Unable to open payment settings. Please contact support.");
                return;
            }
            window.location.href = data.url;
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error, "Unable to open payment settings. Please try again."));
        },
    });
}

export function useCreateBillingCheckout() {
    return useMutation({
        mutationFn: createBillingCheckoutSession,
        onSuccess: (data) => {
            if (!data.url) {
                toast.error("Checkout URL was not returned. Please try again or contact support.");
                return;
            }
            window.location.href = data.url;
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error, "Unable to start checkout. Please try again."));
        },
    });
}
